import React, { useState } from "react";
import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";
import { Modal, ModalBody, Spinner, Form } from "reactstrap";
import { Button, Icon } from "../../../components/Component";
import { authHeader } from "../../../header";

export const VerifyBvnModal = ({ isOpen, toggle }) => {
  const [bvnInput, setBvnInput] = useState("");
  const [searchedBvn, setSearchedBvn] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const resetState = () => {
    setBvnInput("");
    setSearchedBvn("");
    setUserData(null);
    setError(null);
    setIsLoading(false);
    setCopied(false);
  };

  const handleClose = () => {
    resetState();
    if (toggle) toggle();
  };

  const handleBvnChange = (e) => {
    // Only accept numeric input, max 11 digits
    const val = e.target.value.replace(/\D/g, "").slice(0, 11);
    setBvnInput(val);
    if (error) setError(null);
  };

  const handleSearch = async () => {
    const trimmedBvn = bvnInput.trim();
    if (!trimmedBvn) {
      toast.warning("Please enter a BVN");
      return;
    }

    if (trimmedBvn.length !== 11) {
      toast.warning("BVN must be exactly 11 digits");
      return;
    }

    setIsLoading(true);
    setError(null);
    setUserData(null);
    setSearchedBvn(trimmedBvn);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v3/users/${trimmedBvn}/search`,
        {
          headers: authHeader(),
        }
      );

      const result = response?.data?.data || response?.data;
      if (!result || (typeof result === "object" && Object.keys(result).length === 0)) {
        setError(`No customer profile found for BVN: ${trimmedBvn}`);
      } else {
        setUserData(result);
      }
    } catch (err) {
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (err?.response?.status === 404
          ? `No customer profile found for BVN: ${trimmedBvn}`
          : "An error occurred while searching for user profile. Please try again.");
      setError(serverMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!isLoading && bvnInput.length === 11) {
      handleSearch();
    }
  };

  const handleCopy = (text) => {
    if (!text) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      setCopied(true);
      toast.info("BVN copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Safe extraction of minimal values
  const isCompany = Boolean(
    userData?.company && (userData.company.name || userData.company.bvn || userData.company.rcNumber)
  );
  const company = userData?.company || {};

  const fullName = isCompany
    ? company.name || "Corporate Customer"
    : [userData?.firstName, userData?.middleName, userData?.lastName]
        .filter(Boolean)
        .join(" ") ||
      userData?.fullName ||
      "N/A";

  const contactPerson = isCompany
    ? [company.contactFirstName, company.contactMiddleName, company.contactLastName]
        .filter(Boolean)
        .join(" ")
    : null;

  const email = userData?.email || company.contactPersonEmail || "N/A";
  const phone = userData?.phone || company.contactPersonPhoneNo || "N/A";
  const bvnNumber = searchedBvn || company.bvn || userData?.bvn || "N/A";
  const bvnVerified = userData?.bvnVerified;
  const kycVerified = userData?.kyc;
  const ninVerified = userData?.ninVerified;
  const dob = userData?.dateOfBirth
    ? moment(userData.dateOfBirth).format("DD MMM, YYYY")
    : company.contactDOB
    ? moment(company.contactDOB).format("DD MMM, YYYY")
    : "N/A";
  const gender = userData?.gender || userData?.genderCredit || "N/A";
  const status = userData?.status || "ACTIVE";
  const role = userData?.role || (isCompany ? "Corporate" : "Customer");
  const customerId =
    userData?.customerId ||
    company.useraccount?.customerId ||
    (userData?.id ? `#${userData.id}` : "N/A");
  const accountOfficer =
    userData?.accountOfficerEmail || company.accountOfficerName || "N/A";

  const addrObj = userData?.address || userData?.contactDetails || {};
  const resAddr = userData?.residentialAddressResponse || {};
  const street =
    addrObj.streetAddress ||
    addrObj.homeAddress ||
    addrObj.houseNoAddress ||
    resAddr.street ||
    company.companyAddress ||
    "";
  const city = addrObj.city || resAddr.city || company.city || "";
  const state =
    (typeof addrObj.state === "object" ? addrObj.state?.name : addrObj.state) ||
    resAddr.stateName ||
    company.companyState ||
    "";
  const country =
    (typeof addrObj.country === "object" ? addrObj.country?.name : addrObj.country) ||
    resAddr.countryName ||
    "";
  const formattedAddress =
    [street, city, state, country].filter(Boolean).join(", ") || "N/A";

  const initials = isCompany
    ? (company.name || "C").slice(0, 2).toUpperCase()
    : `${userData?.firstName ? userData.firstName.charAt(0) : ""}${
        userData?.lastName ? userData.lastName.charAt(0) : ""
      }`.toUpperCase() || "U";

  return (
    <Modal isOpen={isOpen} toggle={handleClose} className="modal-dialog-centered" size="lg">
      <ModalBody className="p-4">
        <a
          href="#cancel"
          onClick={(e) => {
            e.preventDefault();
            handleClose();
          }}
          className="close"
          aria-label="Close"
        >
          <Icon name="cross-sm"></Icon>
        </a>

        {/* Modal Header */}
        <div className="d-flex align-items-center mb-3">
          <div
            className="user-avatar bg-primary-dim mr-2 d-flex align-items-center justify-content-center"
            style={{ width: "42px", height: "42px", borderRadius: "50%" }}
          >
            <Icon name="shield-check" style={{ fontSize: "1.4rem" }} className="text-primary" />
          </div>
          <div>
            <h5 className="modal-title font-weight-bold mb-0">Verify User BVN</h5>
            <p className="text-muted small mb-0">
              Look up customer profile information using Bank Verification Number (BVN)
            </p>
          </div>
        </div>

        {/* BVN Search Input Box */}
        <div className="card card-bordered mb-3" style={{ backgroundColor: "#f8f9fa" }}>
          <div className="card-inner py-3">
            <Form onSubmit={handleFormSubmit}>
              <div className="row align-items-center">
                <div className="col-md-8 mb-2 mb-md-0">
                  <label className="form-label font-weight-bold small text-muted mb-1">
                    Enter Bank Verification Number (BVN)
                  </label>
                  <div className="form-control-wrap position-relative">
                    <div className="form-icon form-icon-left">
                      <Icon name="search" />
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. 22233344455"
                      value={bvnInput}
                      maxLength={11}
                      onChange={handleBvnChange}
                      autoFocus
                    />
                    {bvnInput && (
                      <div
                        className="form-icon form-icon-right"
                        style={{ cursor: "pointer" }}
                        onClick={() => setBvnInput("")}
                        title="Clear"
                      >
                        <Icon name="cross-sm" />
                      </div>
                    )}
                  </div>
                  <div className="d-flex justify-content-between mt-1">
                    <small className="text-muted">11-digit numeric BVN</small>
                    <small
                      className={
                        bvnInput.length === 11 ? "text-success font-weight-bold" : "text-muted"
                      }
                    >
                      {bvnInput.length}/11 digits
                    </small>
                  </div>
                </div>

                <div className="col-md-4 d-flex align-items-end pt-md-3">
                  <Button
                    color="primary"
                    className="btn-block"
                    type="submit"
                    disabled={bvnInput.length !== 11 || isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Spinner size="sm" className="mr-1" />
                        <span>Searching...</span>
                      </>
                    ) : (
                      <>
                        <Icon name="search" className="mr-1" />
                        <span>Search Profile</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Form>
          </div>
        </div>

        {/* Content Area */}
        {isLoading && (
          <div className="text-center py-5">
            <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
            <p className="mt-3 text-muted">Searching user profile for BVN: {bvnInput}...</p>
          </div>
        )}

        {!isLoading && error && (
          <div className="alert alert-danger d-flex align-items-center my-3" role="alert">
            <Icon name="alert-circle" className="mr-2" style={{ fontSize: "1.5rem" }} />
            <div>
              <strong className="d-block">User Not Found</strong>
              <span>{error}</span>
            </div>
          </div>
        )}

        {!isLoading && !error && !userData && (
          <div className="text-center py-4 px-2">
            <div className="mb-2">
              <Icon name="user-check" style={{ fontSize: "2.8rem", color: "#95aac9" }} />
            </div>
            <h6 className="text-soft">No Search Performed</h6>
            <p className="small text-muted mb-0">
              Provide an 11-digit BVN and click <strong>Search Profile</strong> to verify and view customer details.
            </p>
          </div>
        )}

        {!isLoading && userData && (
          <div className="card card-bordered mt-2">
            {/* User Profile Header Card */}
            <div className="card-inner border-bottom py-3 bg-light">
              <div className="d-flex flex-wrap justify-content-between align-items-center">
                <div className="d-flex align-items-center mb-2 mb-sm-0">
                  <div
                    className="user-avatar bg-primary text-white font-weight-bold mr-3 d-flex align-items-center justify-content-center"
                    style={{ width: "48px", height: "48px", fontSize: "1.1rem", borderRadius: "50%" }}
                  >
                    {initials}
                  </div>
                  <div>
                    <h6 className="font-weight-bold mb-0 text-dark">{fullName}</h6>
                    <span className="small text-muted">
                      {isCompany ? "Corporate Account" : "Individual Account"} {customerId !== "N/A" && `• ID: ${customerId}`}
                    </span>
                  </div>
                </div>

                <div className="d-flex flex-wrap align-items-center">
                  {/* BVN Status Badge */}
                  {bvnVerified ? (
                    <span className="badge badge-dim badge-success mr-2 mb-1">
                      <Icon name="check-circle" className="mr-1" />
                      BVN Verified
                    </span>
                  ) : (
                    <span className="badge badge-dim badge-danger mr-2 mb-1">
                      <Icon name="cross-circle" className="mr-1" />
                      BVN Unverified
                    </span>
                  )}

                  {/* KYC Status Badge */}
                  {kycVerified ? (
                    <span className="badge badge-dim badge-info mr-2 mb-1">
                      KYC Verified
                    </span>
                  ) : (
                    <span className="badge badge-dim badge-warning mr-2 mb-1">
                      KYC Pending
                    </span>
                  )}

                  {/* Account Status Badge */}
                  <span
                    className={`badge badge-dim badge-${
                      status === "ACTIVE" ? "success" : "danger"
                    } mb-1`}
                  >
                    {status}
                  </span>
                </div>
              </div>
            </div>

            {/* Minimal Details Body */}
            <div className="card-inner py-3">
              <h6 className="overline-title text-muted mb-3">Customer Verification Summary</h6>

              <div className="row gy-3">
                {/* BVN */}
                <div className="col-sm-6 col-md-4">
                  <span className="text-muted small d-block">BVN</span>
                  <div className="d-flex align-items-center">
                    <span className="font-weight-bold text-dark">{bvnNumber}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-icon btn-trigger ml-1 text-primary p-0"
                      onClick={() => handleCopy(bvnNumber)}
                      title="Copy BVN"
                      style={{ background: "transparent", border: "none" }}
                    >
                      <Icon name={copied ? "check" : "copy"} />
                    </button>
                  </div>
                </div>

                {/* Email */}
                <div className="col-sm-6 col-md-4">
                  <span className="text-muted small d-block">Email Address</span>
                  <span className="font-weight-bold text-dark text-break">{email}</span>
                </div>

                {/* Phone */}
                <div className="col-sm-6 col-md-4">
                  <span className="text-muted small d-block">Phone Number</span>
                  <span className="font-weight-bold text-dark">{phone}</span>
                </div>

                {/* Date of Birth */}
                <div className="col-sm-6 col-md-4">
                  <span className="text-muted small d-block">Date of Birth</span>
                  <span className="font-weight-bold text-dark">{dob}</span>
                </div>

                {/* Gender */}
                <div className="col-sm-6 col-md-4">
                  <span className="text-muted small d-block">Gender</span>
                  <span className="font-weight-bold text-dark">{gender}</span>
                </div>

                {/* NIN Verification */}
                <div className="col-sm-6 col-md-4">
                  <span className="text-muted small d-block">NIN Verification</span>
                  <span
                    className={`font-weight-bold ${
                      ninVerified ? "text-success" : "text-muted"
                    }`}
                  >
                    {ninVerified ? "Verified" : "Not Verified"}
                  </span>
                </div>

                {/* Account Officer */}
                <div className="col-sm-6 col-md-4">
                  <span className="text-muted small d-block">Account Officer</span>
                  <span className="font-weight-bold text-dark text-break">{accountOfficer}</span>
                </div>

                {/* Role / Type */}
                <div className="col-sm-6 col-md-4">
                  <span className="text-muted small d-block">Role / Type</span>
                  <span className="font-weight-bold text-dark">{role}</span>
                </div>

                {/* Address */}
                <div className="col-sm-12 col-md-8">
                  <span className="text-muted small d-block">Address</span>
                  <span className="font-weight-bold text-dark">{formattedAddress}</span>
                </div>
              </div>

              {/* Corporate Details (Only displayed if company) */}
              {isCompany && (
                <>
                  <div className="border-top mt-3 pt-3">
                    <h6 className="overline-title text-muted mb-2">Company Information</h6>
                    <div className="row gy-2">
                      <div className="col-sm-6 col-md-4">
                        <span className="text-muted small d-block">Company Name</span>
                        <span className="font-weight-bold text-dark">{company.name || "N/A"}</span>
                      </div>
                      <div className="col-sm-6 col-md-4">
                        <span className="text-muted small d-block">RC Number</span>
                        <span className="font-weight-bold text-dark">{company.rcNumber || "N/A"}</span>
                      </div>
                      <div className="col-sm-6 col-md-4">
                        <span className="text-muted small d-block">Contact Person</span>
                        <span className="font-weight-bold text-dark">{contactPerson || "N/A"}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Modal Footer Actions */}
        <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
          <div>
            {userData && (
              <Button
                color="light"
                size="sm"
                outline
                onClick={() => {
                  setUserData(null);
                  setError(null);
                  setBvnInput("");
                }}
              >
                <Icon name="redo" className="mr-1" />
                <span>Search Another BVN</span>
              </Button>
            )}
          </div>
          <Button color="secondary" onClick={handleClose}>
            Close
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};
